// Complete bill configuration for all 8 dynamic pages
import { fetchBillPagesFromSheets } from '../services/googleSheets';

export interface BillPageConfig {
  route: string;
  seoTitle: string;
  seoDescription: string;
  pageTitle: string;
  secondaryTitle: string;
  shortDescription: string;
  billId?: string;
}

// This will be populated from the spreadsheet "Bill Pages" tab
let BILL_PAGES: BillPageConfig[] = [];

// Track last update time and data hash for change detection
let lastUpdateTime = 0;
let dataHash = '';

// Generate a simple hash of the data for change detection
function generateDataHash(data: BillPageConfig[]): string {
  return JSON.stringify(
    data.map((bill) => ({ route: bill.route, pageTitle: bill.pageTitle, lastUpdated: bill.billId })),
  ).slice(0, 100);
}

// Check if data has changed since last load
function hasDataChanged(newData: BillPageConfig[]): boolean {
  const newHash = generateDataHash(newData);
  const changed = newHash !== dataHash;
  if (changed) {
    dataHash = newHash;
    lastUpdateTime = Date.now();
  }
  return changed;
}

// Get last update timestamp
export function getLastUpdateTime(): number {
  return lastUpdateTime;
}

// Function to fetch bill pages data from spreadsheet
export async function loadBillPagesFromSpreadsheet(): Promise<BillPageConfig[]> {
  try {
    // Fetch data from Google Sheets
    const billPages = await fetchBillPagesFromSheets();

    if (billPages.length === 0) {
      return getFallbackData();
    }

    // Check if data has changed and update cache
    const dataChanged = hasDataChanged(billPages);
    if (dataChanged) {
    }

    BILL_PAGES = billPages;
    return billPages;
  } catch (error) {
    const fallbackData = getFallbackData();
    BILL_PAGES = fallbackData;
    return fallbackData;
  }
}

// Force refresh data from spreadsheet (for live updates)
export async function refreshBillPages(): Promise<{ changed: boolean; data: BillPageConfig[] }> {
  try {
    const newData = await loadBillPagesFromSpreadsheet();
    const changed = hasDataChanged(newData);
    return { changed, data: newData };
  } catch (error) {
    throw error;
  }
}

// Fallback data in case spreadsheet is unavailable
export function getFallbackData(): BillPageConfig[] {
  return [
    {
      route: 'time-taken-time-back',
      seoTitle: 'RobertGarza.us Family Court Reform - Time Taken, Time Back Law',
      seoDescription:
        'The Time Taken, Time Back Law restores parenting time lost due to unfounded allegations or precautionary removals, ensuring fairness in custody proceedings.',
      pageTitle: 'Time Taken Time Back',
      secondaryTitle: 'Detour False Allegations',
      shortDescription:
        'This law protects parents from false allegations by ensuring lost custody time is restored, discouraging dishonest tactics and false allegations.',
    },
    {
      route: 'a-new-start-at-equality',
      seoTitle: 'RobertGarza.us Family Court Reform - A New Start at Equality',
      seoDescription:
        "Establishing equal custody as the starting point in divorce proceedings, promoting fairness and children's best interests.",
      pageTitle: 'A New Start at Equality',
      secondaryTitle: 'Equal Custody for All',
      shortDescription:
        'This law establishes equal custody as the starting point, ensuring both parents have meaningful relationships with their children.',
    },
    {
      route: 'three-strikes',
      seoTitle: 'RobertGarza.us Family Court Reform - Three Strikes Law',
      seoDescription:
        'Enhanced penalties for repeat family court violations, creating accountability and protecting children from ongoing abuse.',
      pageTitle: 'Three Strikes Law',
      secondaryTitle: 'Accountability in Family Court',
      shortDescription:
        'Enhanced penalties for repeat violators ensure accountability and protect families from ongoing court abuse.',
    },
    {
      route: 'child-support-dual-calculation-reform',
      seoTitle: 'RobertGarza.us Family Court Reform - Child Support Dual Calculation Reform',
      seoDescription:
        "Reforming child support calculations to ensure fairness and consider both parents' circumstances and time with children.",
      pageTitle: 'Child Support Dual Calculation Reform',
      secondaryTitle: 'Fair Support for All Families',
      shortDescription:
        "Reform child support calculations to be fair, transparent, and consider both parents' circumstances and parenting time.",
    },
    {
      route: 'cameras-in-courtrooms',
      seoTitle: 'RobertGarza.us Family Court Reform - Cameras in Courtrooms',
      seoDescription:
        'Mandating cameras in family courtrooms to ensure transparency, accountability, and protection of constitutional rights.',
      pageTitle: 'Cameras in Courtrooms',
      secondaryTitle: 'Transparency and Accountability',
      shortDescription:
        'Mandatory recording in family courtrooms ensures transparency, protects constitutional rights, and prevents judicial misconduct.',
    },
    {
      route: 'justice-delayed-is-justice-denied',
      seoTitle: 'RobertGarza.us Family Court Reform - Justice Delayed is Justice Denied',
      seoDescription:
        'Ensuring timely resolution of family court cases to prevent prolonged separation and emotional harm to children and families.',
      pageTitle: 'Justice Delayed is Justice Denied',
      secondaryTitle: 'Timely Resolution for Families',
      shortDescription:
        'Mandatory timelines for family court proceedings prevent prolonged cases that harm children and families.',
    },
    {
      route: 'litigant-access-economy-act',
      seoTitle: 'RobertGarza.us Family Court Reform - Litigant Access and Economy Act',
      seoDescription:
        'Making family court more accessible and affordable for all families, reducing financial barriers to justice.',
      pageTitle: 'The Litigant Access and Economy Act',
      secondaryTitle: 'Affordable Justice for All',
      shortDescription:
        'Reducing court costs and improving access to ensure all families can afford justice in family court proceedings.',
    },
    {
      route: 'appealable-temporary-orders',
      seoTitle: 'RobertGarza.us Family Court Reform - Appealable Temporary Orders',
      seoDescription:
        'Ensuring temporary family court orders can be appealed, protecting due process rights and preventing irreversible harm.',
      pageTitle: 'Appealable Temporary Orders',
      secondaryTitle: 'Protecting Due Process Rights',
      shortDescription:
        'Allowing appeals of temporary orders ensures due process and prevents irreversible harm from rushed decisions.',
    },
    {
      route: 'child-support-integrity-act',
      seoTitle: 'RobertGarza.us Family Court Reform - Child Support Integrity Act',
      seoDescription:
        'The Child Support Integrity Act ensures fair and accountable child support enforcement practices worldwide.',
      pageTitle: 'Child Support Integrity Act',
      secondaryTitle: 'Ensuring Fair Child Support Practices',
      shortDescription:
        'This legislation establishes DNA testing rights and transparency in child support enforcement, ensuring biological accuracy and fair practices for all families.',
    },
    {
      route: 'reimbursement-fees-parental-alienation',
      seoTitle: 'RobertGarza.us Family Court Reform - Reimbursement of Fees for Parental Alienation',
      seoDescription:
        'The Reimbursement of Fees for Parental Alienation Act holds alienating parents financially accountable, requiring repayment of legal costs incurred by the targeted parent.',
      pageTitle: 'Reimbursement of Fees for Parental Alienation',
      secondaryTitle: 'Financial Accountability for Alienation',
      shortDescription:
        'Requires parents who engage in parental alienation to reimburse the targeted parent for legal fees, creating financial deterrents against alienating behavior.',
    },
    {
      route: 'family-rights-due-process-act',
      seoTitle: 'RobertGarza.us Family Court Reform - The Family Rights and Due Process Act',
      seoDescription:
        'The Family Rights and Due Process Act ensures constitutional due process protections in family court proceedings, safeguarding parental rights and preventing arbitrary judicial decisions.',
      pageTitle: 'The Family Rights and Due Process Act',
      secondaryTitle: 'Constitutional Protections for Families',
      shortDescription:
        'Establishes mandatory due process protections in family court, ensuring parents receive fair hearings and constitutional rights are upheld in custody and support proceedings.',
    },
    {
      route: 'parent-child-restoration-act',
      seoTitle: 'Parent-Child Restoration Act | Robert Garza Reform',
      seoDescription:
        'Explore the Parent-Child Restoration Act, which restores custody and parenting time after allegations are unsubstantiated while preserving child protections.',
      pageTitle: 'Parent-Child Restoration Act',
      secondaryTitle: 'Prompt Restoration After Unsubstantiated Allegations',
      shortDescription:
        'Requires courts to promptly lift unsupported custody or visitation restrictions, restore the prior parenting order, and award compensatory parenting time while preserving necessary child protections.',
    },
    {
      route: 'best-interest-of-the-child-definition-act',
      seoTitle: 'RobertGarza.us Family Court Reform - Best Interest of the Child Definition Act',
      seoDescription:
        'The Best Interest of the Child Definition Act establishes a clear statutory definition to guide custody determinations and prevent inconsistent judicial interpretations in family courts.',
      pageTitle: 'Best Interest of the Child Definition Act',
      secondaryTitle: "Defining What's Best for Children in Law",
      shortDescription:
        "Establishes a precise statutory definition of 'best interest of the child' to prevent arbitrary judicial discretion and ensure consistent, fair custody outcomes for all families.",
    },
    {
      route: 'judicial-accountability-referral-act',
      seoTitle: 'Judicial Accountability Referral Act | Robert Garza Reform',
      seoDescription:
        'Explore the Judicial Accountability Referral Act, a proposal to refer family court judicial conduct to an independent review commission so dismissed families have a path to accountability.',
      pageTitle: 'Judicial Accountability Referral Act',
      secondaryTitle: 'Independent Review of Judicial Conduct',
      shortDescription:
        'Proposes a referral pathway that brings judicial conduct concerns from family court proceedings before an independent review commission, giving dismissed families a real path to accountability.',
    },
  ];
}

// Bill titles for the legislator meeting request form "Bill(s) to Be Discussed" field.
// Derived from the shared bill data so any bill added to getFallbackData() is selectable automatically.
// Keep this derived; do not hardcode bill titles in the meeting form.
export function getBillOptions(): string[] {
  return getFallbackData()
    .map((bill) => bill.pageTitle)
    .sort((a, b) => a.localeCompare(b));
}

// Initialize bill pages (call this on app startup)
export async function initializeBillPages(): Promise<void> {
  const data = await loadBillPagesFromSpreadsheet();
  BILL_PAGES = data;
}

// Helper function to get bill config by route
export function getBillConfig(route: string | undefined): BillPageConfig | undefined {
  return BILL_PAGES.find((bill) => bill.route === route);
}

// Helper function to get all bill routes for sitemap/navigation
export function getAllBillRoutes(): string[] {
  return BILL_PAGES.map((bill) => bill.route);
}

// Helper function to get all bill pages data
export function getAllBillPages(): BillPageConfig[] {
  return BILL_PAGES;
}

// Problem-first navigation copy lives beside the shared catalog, never in the hero UI.
// Summaries are grounded in public/bills/billIndex.json's whyItMatters content.
export const BILL_CONCERN_TOPICS = [
  { id: 'parenting', title: 'Parenting time', description: 'Equal time, missed visits, and restoring contact' },
  { id: 'decisions', title: 'Court decisions', description: 'Delays, appeals, and fair standards' },
  { id: 'costs', title: 'Support & costs', description: 'Child support, legal fees, and attending court' },
  { id: 'accountability', title: 'Court accountability', description: 'Courtroom records and judicial conduct' },
] as const;

export type BillConcernTopic = (typeof BILL_CONCERN_TOPICS)[number]['id'];

interface BillConcern {
  topic: BillConcernTopic;
  problem: string;
}

const BILL_CONCERNS: Record<string, BillConcern> = {
  'a-new-start-at-equality': { topic: 'parenting', problem: 'Equal parenting time is not the starting point.' },
  'time-taken-time-back': { topic: 'parenting', problem: 'Time lost to unproven allegations is never made up.' },
  'parent-child-restoration-act': {
    topic: 'parenting',
    problem: 'Restrictions remain after allegations are unsubstantiated.',
  },
  'three-strikes': { topic: 'parenting', problem: 'The other parent repeatedly ignores custody orders.' },
  'justice-delayed-is-justice-denied': { topic: 'decisions', problem: 'I am waiting too long for a court ruling.' },
  'appealable-temporary-orders': { topic: 'decisions', problem: 'I cannot appeal a harmful temporary order.' },
  'family-rights-due-process-act': {
    topic: 'decisions',
    problem: 'My rights are restricted without clear evidence or written reasons.',
  },
  'best-interest-of-the-child-definition-act': {
    topic: 'decisions',
    problem: '“Best interest of the child” has no clear standard.',
  },
  'child-support-dual-calculation-reform': {
    topic: 'costs',
    problem: 'Child support does not reflect our shared parenting time.',
  },
  'child-support-integrity-act': { topic: 'costs', problem: 'Child support was ordered without verifying parentage.' },
  'reimbursement-fees-parental-alienation': {
    topic: 'costs',
    problem: 'I am paying to repair harm from parental alienation.',
  },
  'litigant-access-economy-act': {
    topic: 'costs',
    problem: 'Work, travel, or childcare makes attending court difficult.',
  },
  'cameras-in-courtrooms': {
    topic: 'accountability',
    problem: 'There is no complete recording of what happens in court.',
  },
  'judicial-accountability-referral-act': {
    topic: 'accountability',
    problem: 'Serious judicial violations found on appeal need independent review.',
  },
};

export function getBillConcernOptions() {
  return getFallbackData().flatMap((bill) => {
    const concern = BILL_CONCERNS[bill.route];
    return concern ? [{ ...concern, route: bill.route, pageTitle: bill.pageTitle }] : [];
  });
}
